import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useRef, useEffect } from 'react';

function getVideoEmbedInfo(url) {
  if (!url) return null;
  const ytPatterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pat of ytPatterns) {
    const m = url.match(pat);
    if (m) return {
      isEmbed: true,
      src: `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&loop=1&controls=0&playlist=${m[1]}&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&disablekb=1&fs=0&playsinline=1`
    };
  }
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) return {
    isEmbed: true,
    src: `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&background=1`
  };
  return { isEmbed: false, src: url };
}

function resolveUrl(url) {
  if (!url) return '';
  if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function HeroSection({ media }) {
  const heroMedia = media[0];
  const bgUrl = resolveUrl(heroMedia?.optimized_url || heroMedia?.url || '');
  const isVideo = heroMedia?.media_type === 'video';
  const videoUrl = heroMedia?.video_url || '';
  const hasVideo = isVideo || videoUrl;
  const embedInfo = hasVideo ? getVideoEmbedInfo(videoUrl || bgUrl) : null;
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background Media */}
      {hasVideo && embedInfo ? (
        embedInfo.isEmbed ? (
          <div className="absolute inset-0 overflow-hidden">
            <iframe
              src={embedInfo.src}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ width: '177.78vh', minWidth: '100%', height: '100vh', minHeight: '100%', pointerEvents: 'none', border: 'none' }}
              allow="autoplay; fullscreen"
              title="Hero Video"
            />
          </div>
        ) : (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            src={resolveUrl(embedInfo.src)}
            muted loop playsInline
          />
        )
      ) : bgUrl ? (
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center animate-ken-burns"
          style={{ backgroundImage: `url(${bgUrl})` }}
        />
      ) : null}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/65 z-[1]" />

      {/* Noise texture */}
      <div className="absolute inset-0 bg-noise opacity-[0.03] z-[2]" />

      {/* Thin gold line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gold/20 z-10" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-gold/80 text-[10px] md:text-xs tracking-[0.5em] uppercase mb-8 font-body font-light">
            Resorts World Las Vegas
          </p>

          <h1
            className="font-heading text-7xl sm:text-8xl md:text-9xl lg:text-[11rem] font-bold tracking-[-0.02em] leading-[0.85] pb-2 sm:pb-0 hero-title-sweep"
            data-testid="hero-title"
          >
            OZFEST
          </h1>

          <div className="flex items-center justify-center gap-4 my-8">
            <div className="w-12 md:w-20 h-[1px] bg-gold/50" />
            <div className="w-1.5 h-1.5 rotate-45 border border-gold/50" />
            <div className="w-12 md:w-20 h-[1px] bg-gold/50" />
          </div>

          <p className="text-sm md:text-base text-zinc-400 font-body font-light tracking-[0.1em] max-w-xl mx-auto">
            Sport. Culture. Energy. Australia Takes Over Vegas.
          </p>
        </motion.div>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 mt-14"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <button
            data-testid="explore-btn"
            onClick={() => scrollTo('intro')}
            className="btn-glow px-8 py-3.5 bg-gold text-black font-heading text-xs tracking-[0.25em] uppercase hover:bg-white transition-colors duration-300"
          >
            Explore OZFEST
          </button>
          <button
            data-testid="highlights-btn"
            onClick={() => scrollTo('experience')}
            className="px-8 py-3.5 border border-gold/40 text-gold font-heading text-xs tracking-[0.25em] uppercase hover:bg-gold hover:text-black transition-all duration-300"
          >
            View Highlights
          </button>
          <button
            data-testid="partner-btn"
            onClick={() => scrollTo('contact')}
            className="px-8 py-3.5 border border-white/15 text-white/70 font-heading text-xs tracking-[0.25em] uppercase hover:border-gold/50 hover:text-gold transition-all duration-300"
          >
            Partner With Us
          </button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-10 cursor-pointer"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          onClick={() => scrollTo('intro')}
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-5 h-5 text-gold/40" />
        </motion.div>
      </div>
    </section>
  );
}
